import type { RaspadinhaReadingCriterion } from '@/types/raspadinha-campaign'

export const MAX_READING_CRITERIA = 50
export const MAX_READING_CRITERION_TERM_LENGTH = 120

function buildReadingCriterionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `reading-criterion-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function normalizeReadingCriterionTerm(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLocaleLowerCase('pt-BR')
}

export function createReadingCriterion(term: string): RaspadinhaReadingCriterion {
  return {
    id: buildReadingCriterionId(),
    term: term.trim().replace(/\s+/g, ' ').slice(0, MAX_READING_CRITERION_TERM_LENGTH),
  }
}

export function ensureReadingCriteria(
  criteria: RaspadinhaReadingCriterion[] | undefined
): RaspadinhaReadingCriterion[] {
  return (criteria ?? []).slice(0, MAX_READING_CRITERIA).map((criterion) => ({
    id: criterion.id,
    term: criterion.term ?? '',
  }))
}

export function isDuplicateReadingCriterion(
  criteria: RaspadinhaReadingCriterion[],
  term: string,
  excludeId?: string
): boolean {
  const normalized = normalizeReadingCriterionTerm(term)
  if (!normalized) return false

  return criteria.some(
    (criterion) =>
      criterion.id !== excludeId && normalizeReadingCriterionTerm(criterion.term) === normalized
  )
}

export type AddReadingCriterionResult = {
  criteria: RaspadinhaReadingCriterion[]
  error: string | null
}

export function addReadingCriterion(
  criteria: RaspadinhaReadingCriterion[],
  termInput: string
): AddReadingCriterionResult {
  const term = termInput.trim().replace(/\s+/g, ' ')

  if (!term) {
    return { criteria, error: 'Informe o termo do critério de leitura.' }
  }

  if (term.length > MAX_READING_CRITERION_TERM_LENGTH) {
    return {
      criteria,
      error: `Cada critério de leitura deve ter no máximo ${MAX_READING_CRITERION_TERM_LENGTH} caracteres.`,
    }
  }

  if (criteria.length >= MAX_READING_CRITERIA) {
    return {
      criteria,
      error: `É permitido cadastrar no máximo ${MAX_READING_CRITERIA} critérios de leitura.`,
    }
  }

  if (isDuplicateReadingCriterion(criteria, term)) {
    return { criteria, error: 'Este termo já está na lista.' }
  }

  return {
    criteria: [...criteria, createReadingCriterion(term)],
    error: null,
  }
}

export function updateReadingCriterion(
  criteria: RaspadinhaReadingCriterion[],
  criterionId: string,
  termInput: string
): RaspadinhaReadingCriterion[] {
  const term = termInput.slice(0, MAX_READING_CRITERION_TERM_LENGTH)

  return criteria.map((criterion) =>
    criterion.id === criterionId
      ? {
          ...criterion,
          term,
        }
      : criterion
  )
}

export function removeReadingCriterion(
  criteria: RaspadinhaReadingCriterion[],
  criterionId: string
): RaspadinhaReadingCriterion[] {
  return criteria.filter((criterion) => criterion.id !== criterionId)
}

export function validateReadingCriteria(
  criteria: RaspadinhaReadingCriterion[] | undefined
): string | null {
  const list = ensureReadingCriteria(criteria)

  if (list.length > MAX_READING_CRITERIA) {
    return `É permitido cadastrar no máximo ${MAX_READING_CRITERIA} critérios de leitura.`
  }

  const seen = new Set<string>()

  for (const criterion of list) {
    const term = criterion.term.trim().replace(/\s+/g, ' ')
    if (!term) {
      return 'Informe o texto de cada critério de leitura.'
    }

    if (term.length > MAX_READING_CRITERION_TERM_LENGTH) {
      return `Cada critério de leitura deve ter no máximo ${MAX_READING_CRITERION_TERM_LENGTH} caracteres.`
    }

    const normalized = normalizeReadingCriterionTerm(term)
    if (seen.has(normalized)) {
      return 'Não é permitido cadastrar critérios de leitura duplicados.'
    }
    seen.add(normalized)
  }

  return null
}
